import { faArrowRight, faTimesCircle } from '@fortawesome/pro-solid-svg-icons'
import { ActionButton, HeadingItem } from 'components'
import { TripContext } from 'contexts'
import { CheckBox } from 'devextreme-react'
import { Popup } from 'devextreme-react/popup'
import React, { FC, Fragment, useContext, useEffect, useState } from 'react'
import { useNavigation, useOrderItemService, useReturnItemService } from 'services'
import { Temperature } from 'services/enums'
import { LicensePlate, LineItem, Order, OrderItem, OrderItemChange, ReturnItem } from 'services/types'
import { CheckBoxItem } from 'types'
import './recap-popup.scss'

type Props = {
    tripId: number | undefined,
    stopId: number | undefined,
    isNavFromStopOverview: boolean,
    onClickCancel: () => void,
}

export const RecapPopupContent: FC<Props> = ({ tripId, stopId, isNavFromStopOverview, onClickCancel }) => {
    const { navigateTo } = useNavigation()
    const tripContext = useContext(TripContext)
    const { isOrderItemChangeComplete, isOrderItemComplete } = useOrderItemService()
    const { returnItemsNotCompletedCount } = useReturnItemService()
    const [recapTemperatures, setRecapTemperatures] = useState<CheckBoxItem[]>([])
    const [completedTemperatures, setCompletedTemperatures] = useState<string[]>([])

    const orders: Order[] = tripContext.orders.filter(lp => lp.stopId === stopId) ?? []
    const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id === item.orderId))
    const returnItems: ReturnItem[] = tripContext.returnItems.filter(returnItem => orders.some(order => order.id === returnItem.orderId)) ?? []
    const lineItems: LineItem[] = tripContext.lineItems.filter(lineItem => (stopItems.some(orderItem => lineItem.id === orderItem.lineItemId) || returnItems.some(returnItem => lineItem.id === returnItem.lineItemId))) ?? []
    const stopLps: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId == stopId) ?? []
    const orderItemsChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => stopItems.some(orderItem => orderItem.id == change.orderItemId)) ?? []

    const notApprovedOrderItemChanges: OrderItemChange[] = orderItemsChanges.filter(change => change.isChangeApproved != true)
    const notApprovedStopItems: OrderItem[] = stopItems.filter(item => notApprovedOrderItemChanges.some(change => change.orderItemId == item.id))
    const notApprovedLineItems: LineItem[] = lineItems.filter(li => notApprovedStopItems.some(item => item.lineItemId == li.id))

    const [notApprovedDryItemChanges, notApprovedFrozenItemChanges, notApprovedRefrigeratoredItems] = [
        notApprovedLineItems.filter(li => li.temperature === Temperature.Dry),
        notApprovedLineItems.filter(li => li.temperature === Temperature.Frozen),
        notApprovedLineItems.filter(li => li.temperature === Temperature.Refrigerated)
    ]

    const [dryLineItem, frozenLineItem, refLineItem] = [
        lineItems.filter(li => li.temperature === Temperature.Dry),
        lineItems.filter(li => li.temperature === Temperature.Frozen),
        lineItems.filter(li => li.temperature === Temperature.Refrigerated),
    ]

    const getNotSignedOrders = (temp: Temperature): Order[] => {
        let orderItems: OrderItem[] = stopItems.filter(oi => lineItems.some(li => li.temperature === temp && li.id === oi.lineItemId))
        let returns: ReturnItem[] = returnItems.filter(ri => lineItems.some(li => li.temperature === temp && li.id === ri.lineItemId))
        return orders.filter(o => orderItems.some(oi => oi.orderId === o.id) && o.signatureId === null || returns.some(ri => ri.orderId == o.id) && o.signatureId === null)
    }

    const getNotVerifiedItems = (temp: Temperature): number => {
        const orderItems = stopItems.filter(orderItem => lineItems.find(lineItem => lineItem.id == orderItem.lineItemId)?.temperature === temp)
        const returns = returnItems.filter(item => lineItems.find(lineItem => lineItem.id == item.lineItemId)?.temperature === temp)
        return orderItems.filter(orderItem => !isOrderItemComplete(orderItem)).length + returns.filter(item => item.verifiedMethod == null).length
    }

    const getNotVerifiedLp = (temp: Temperature): number => {
        return stopLps.filter(lp => lp.temperature === temp && lp.verifiedMethod == null).length
    }

    const changesNotCompletedCount = (temp: Temperature): number => {
        const orderItemsByTemp = stopItems.filter(orderItem => lineItems.find(lineItem => lineItem.id == orderItem.lineItemId)?.temperature === temp)
        const returnsByTemp = returnItems.filter(item => lineItems.find(lineItem => lineItem.id == item.lineItemId)?.temperature === temp)
        let orderItemsChangesByTemp: OrderItemChange[] = orderItemsChanges.filter(x => orderItemsByTemp.some(item => item.id == x.orderItemId)) ?? []
        let areOrderItemChangesComplete: OrderItemChange[] = orderItemsChangesByTemp.filter(x => !isOrderItemChangeComplete(x)) ?? []
        return areOrderItemChangesComplete.length + returnItemsNotCompletedCount(returnsByTemp)
    }

    const addRecapTemperature = (itemId: string, itemText: string, notVerifiedItemCount: number, notVerifiedLpCount: number, changesNotCompletedCount: number): void => {

        const itemMessage: string = `You still have ${notVerifiedItemCount} item${notVerifiedItemCount == 1 ? '' : 's'} to verify`
        const lpMessage: string = `You still have ${notVerifiedLpCount} LP${notVerifiedLpCount == 1 ? '' : 's'} to verify`
        const changesMessage: string = `Complete ${changesNotCompletedCount} unfinished change${changesNotCompletedCount == 1 ? '' : 's'}`

        setRecapTemperatures(temp =>
            [...temp,
            {
                id: itemId,
                isSelected: (notVerifiedItemCount > 0 || notVerifiedLpCount > 0 || changesNotCompletedCount > 0) ? false : true,
                text: itemText,
                isEnable: (notVerifiedItemCount > 0 || notVerifiedLpCount > 0 || changesNotCompletedCount > 0) ? false : true,
                message: notVerifiedItemCount > 0 ? itemMessage : (notVerifiedLpCount > 0 ? lpMessage : (changesNotCompletedCount > 0 ? changesMessage : ''))
            }
            ]
        )
    }

    const addCompletedTemperature = (itemText: string): void => {
        setCompletedTemperatures(temp => [...temp, itemText])
    }

    useEffect(() => {
        // Dry
        if (dryLineItem.length > 0) {
            if (getNotSignedOrders(Temperature.Dry).length > 0 && isNavFromStopOverview) {
                addRecapTemperature(Temperature.Dry, 'Dry', getNotVerifiedItems(Temperature.Dry), getNotVerifiedLp(Temperature.Dry), changesNotCompletedCount(Temperature.Dry))
            }
            else {
                addCompletedTemperature('Dry')
            }
        }
        // Frozen
        if (frozenLineItem.length > 0) {
            if (getNotSignedOrders(Temperature.Frozen).length > 0 && isNavFromStopOverview) {
                addRecapTemperature(Temperature.Frozen, 'Frozen', getNotVerifiedItems(Temperature.Frozen), getNotVerifiedLp(Temperature.Frozen), changesNotCompletedCount(Temperature.Frozen))
            }
            else {
                addCompletedTemperature('Frozen')
            }
        }
        // Ref
        if (refLineItem.length > 0) {
            if (getNotSignedOrders(Temperature.Refrigerated).length > 0 && isNavFromStopOverview) {
                addRecapTemperature(Temperature.Refrigerated, 'Ref', getNotVerifiedItems(Temperature.Refrigerated), getNotVerifiedLp(Temperature.Refrigerated), changesNotCompletedCount(Temperature.Refrigerated))
            }
            else {
                addCompletedTemperature('Ref')
            }
        }
    }, [])

    const onRecapTempsChanged = (selectedTemp: string | number, isSelected: boolean): void => {
        let tempIndex: number = recapTemperatures.findIndex(temp => temp.id === selectedTemp)
        recapTemperatures[tempIndex].isSelected = isSelected
        setRecapTemperatures([...recapTemperatures])
    }

    const onClickRecap = (): void => {
        const isDrySelected = recapTemperatures.some(t => t.text === 'Dry' && t.isSelected);
        const isFrozenSelected = recapTemperatures.some(t => t.text === 'Frozen' && t.isSelected);
        const isRefSelected = recapTemperatures.some(t => t.text === 'Ref' && t.isSelected);

        if ((isDrySelected && notApprovedDryItemChanges.length > 0) || (isFrozenSelected && notApprovedFrozenItemChanges.length > 0) || (isRefSelected && notApprovedRefrigeratoredItems.length > 0)) {
            navigateTo(`change-approval?tripId=${tripId}&stopId=${stopId}&dry=${isDrySelected}&frozen=${isFrozenSelected}&ref=${isRefSelected}`)
        }
        else {
            navigateTo(`recap?tripId=${tripId}&stopId=${stopId}&readOnly=false&dry=${isDrySelected}&frozen=${isFrozenSelected}&ref=${isRefSelected}`)
        }

    }

    const onClickView = (selectedTemp: string): void => {
        const isDrySelected = selectedTemp === 'Dry';
        const isFrozenSelected = selectedTemp === 'Frozen';
        const isRefSelected = selectedTemp === 'Ref';
        navigateTo(`recap?tripId=${tripId}&stopId=${stopId}&readOnly=true&dry=${isDrySelected}&frozen=${isFrozenSelected}&ref=${isRefSelected}`)
    }

    return (
        <>
            <HeadingItem text={'Select Temperatures to Recap'}
                rightHandIcon={{ icon: faTimesCircle, size: '2x', color: 'black' }}
                onClick={onClickCancel}
            />
            <div>
                {recapTemperatures.length > 0 &&
                    <div>
                        <div className={'recap-card-temperature'}>
                            <div className={'recap-checkbox-list-card'}>
                                {recapTemperatures.map(d =>
                                    <div className={'recap-checkbox-list-row'} key={d.id}>
                                        <CheckBox
                                            value={d.isSelected}
                                            disabled={!d.isEnable}
                                            onValueChanged={(args) => { onRecapTempsChanged(d.id, args.value) }}
                                        />
                                        <div className={'recap-checkbox-list-row'}>
                                            <div className={'recap-checkbox-list-text'}>{d.text ?? d.id}</div>
                                            <div className={'recap-checkbox-list-message'}>{d.message}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                }
                    <div className={'recap-card-action'}>
                        <ActionButton
                            enabled={recapTemperatures.filter(temp => temp.isSelected).length > 0}
                            icon={faArrowRight}
                            iconSize={'1x'}
                            buttonText={'Next'}
                            buttonColor={'#82BC24'}
                            onClick={onClickRecap}
                        />
                    </div>
            </div>
            <div>
                {completedTemperatures.length > 0 &&
                    <div className={'card-material'}>
                        <div className={'recap-card-heading'}>
                            <div className={'recap-card-text'}>Completed Recaps</div>
                        </div>
                        <div className={'recap-card-temperature'}>
                            <div className={'recap-checkbox-list-card'}>
                                {completedTemperatures.map((ct, index) =>
                                    <div className={'recap-checkbox-list-row'} key={index}>
                                        <div className={'recap-checkbox-list-label'}>{ct}</div>
                                        <div className={'recap-checkbox-list-action'}>
                                            <ActionButton
                                                icon={faArrowRight}
                                                iconSize={'1x'}
                                                buttonText={'View'}
                                                buttonColor={'#F2994A'}
                                                onClick={() => onClickView(ct)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                }
            </div>
        </>
    )
}